import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";

import { getConversionRates } from "@stadium/redux-utils/selectors/configSelector";

import { useConfig } from "@stadium/hooks/lib/use-config";

import { useSendItemsContext } from "@contexts/send-items-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useStoreContext } from "@components/store/provider";

import { getCountry } from "@selectors/country";
import { getCartOrderId, getCartOrderCurrency, getCartOrder } from "@store/selectors/cart";

import { getStoreCurrencyCode } from "@selectors/store";

import { updateCartOrder, updateCart } from "@slices/cart";

import { isSwagProduct } from "@helpers/products/types";
import { formatOrder } from "@stadium/checkout/src/helpers/order";
import { batchPopulateOrder, updateOrderDetails } from "@actions/order";

import usePopulateLocalItems from "@hooks/cart/use-populate-local-items";

import { useWindowStorage } from "@hooks/use-window-storage";

import { getBatchPopulatePayloadData } from "@helpers/send-items";
import { convertPrice } from "@helpers/currency";
import { getStorePreviewModeCartKey } from "@helpers/store";

import { SEND_TO_MYSELF } from "@constants/navigation";
import { SM_PROGRAM_ID } from "@constants/app-defaults";

import { SPREE_ORDER_PRODUCTS_SETS } from "@constants/products/detail";
import { CART_RESPONSE_TYPE } from "@constants/orders/cart";

const emptyArr = [];

const updatedProductsWithVariant = (item) => ({
  ...item,
  ...(item.productData || item.product),
  id: item.id,
});

const getOrderProducts = (order) => [
  ...(order?.lineItems || emptyArr),
  ...(order?.productSets || emptyArr),
  ...(order?.medleyItemSets || emptyArr),
];

const mergeOrderProductsWithCart = (order, cartOrder) => {
  const cartProductsById = getOrderProducts(cartOrder).reduce(
    (acc, item) => ({ ...acc, [item.id]: item }),
    {},
  );

  return getOrderProducts(order).map((item) => ({
    ...cartProductsById[item.id],
    ...item,
  }));
};

const getVariantChangeProducts = (products) => {
  const { swagProductData, nonSwagProductData } = products.map(updatedProductsWithVariant).reduce(
    (mapping, product) => {
      const { intent = "" } = product;
      if (isSwagProduct(intent)) mapping.swagProductData.push(product);
      else mapping.nonSwagProductData.push(product);
      return mapping;
    },
    {
      swagProductData: [],
      nonSwagProductData: [],
    },
  );

  const swagVariantChangeProducts = swagProductData.filter(({ isMaster }) => !isMaster) || emptyArr;

  const nonSwagVariantChangeProducts =
    nonSwagProductData.filter(
      ({
        type,
        dietaryPreference = "",
        dietaryRestriction = [],
        restrictions,
        isRestrictionsSelected,
      }) =>
        type === SPREE_ORDER_PRODUCTS_SETS &&
        (dietaryPreference ||
          !isEmpty(dietaryRestriction) ||
          !isEmpty(restrictions) ||
          isRestrictionsSelected),
    ) || emptyArr;

  return { swagVariantChangeProducts, nonSwagVariantChangeProducts };
};

export const useHandleSendToManyToggle = () => {
  const dispatch = useDispatch();
  const currencyRef = useRef(null);
  const { countryCode, storeNumber } = useStoreContext();

  const conversionRates = useConfig(getConversionRates);
  const selectedCountry = useSelector(getCountry);
  const orderId = useSelector(getCartOrderId);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const cartOrder = useSelector(getCartOrder);

  const orderCurrencyCode = useSelector(getCartOrderCurrency);
  currencyRef.current = orderCurrencyCode || storeCurrencyCode;

  const { sendItemsSelection, isSendToMyself } = useSendItemsContext();

  const { isGuestOrPreviewMode } = usePreviewModeContext();

  const [, setLocalCartOrder] = useWindowStorage(
    getStorePreviewModeCartKey(storeNumber, countryCode, isSendToMyself),
    isGuestOrPreviewMode ? cartOrder : undefined,
  );

  const { updateSendToManyToggleItemsInLocalCart } = usePopulateLocalItems();

  useEffect(() => {
    if (isGuestOrPreviewMode) setLocalCartOrder(cartOrder);
  }, [isSendToMyself]);

  const getPrice = ({ price, storePrice }) => {
    return storePrice
      ? convertPrice({
          value: storePrice,
          from: storeCurrencyCode,
          to: currencyRef.current,
          conversionRates,
        })
      : price;
  };

  const updateCartProduct = async (orderData) => {
    const order = formatOrder(orderData);
    const enrichedProducts = mergeOrderProductsWithCart(order, cartOrder);
    const { swagVariantChangeProducts, nonSwagVariantChangeProducts } =
      getVariantChangeProducts(enrichedProducts);

    dispatch(updateCartOrder(order));

    if (isEmpty(swagVariantChangeProducts) && isEmpty(nonSwagVariantChangeProducts)) {
      return;
    }

    const productPayloadData = getBatchPopulatePayloadData({
      swagProductsThatRequiredVariantsChange: swagVariantChangeProducts,
      nowSwagProductsThatRequiredVariantsChange: nonSwagVariantChangeProducts,
    });
    currencyRef.current = order?.currency || storeCurrencyCode;
    const payload = {
      currency_code: order?.currency || storeCurrencyCode,
      ...productPayloadData,
      line_items_attributes: productPayloadData.line_items_attributes.map((attributes) => ({
        ...attributes,
        price: getPrice({ price: attributes.price, storePrice: attributes.price }),
      })),
      product_sets_attributes: productPayloadData.product_sets_attributes.map((attributes) => ({
        ...attributes,
        price: getPrice({ price: attributes.price, storePrice: attributes.storePrice }),
      })),
    };
    return dispatch(batchPopulateOrder(orderId, payload)).then((order) =>
      dispatch(updateCart(order)),
    );
  };

  const updateOrderData = (isSendToOther, sendItemsStatus) =>
    dispatch(
      updateOrderDetails(
        orderId,
        {
          program_id: SM_PROGRAM_ID,
          send_to_many: isSendToOther,
          country_id: selectedCountry.id,
          ...(sendItemsStatus !== SEND_TO_MYSELF && {
            gift_detail_attributes: {},
            treat_name: orderId,
          }),
        },
        {},
        CART_RESPONSE_TYPE,
      ),
    );

  const updateOrderStatus = async (sendItemsStatus) => {
    if (!orderId || sendItemsStatus === sendItemsSelection) return;
    const isSendToOther = sendItemsStatus !== SEND_TO_MYSELF;
    if (isGuestOrPreviewMode) return updateSendToManyToggleItemsInLocalCart(isSendToOther);
    updateOrderData(isSendToOther, sendItemsStatus)
      .then((response) => updateCartProduct(response))
      .catch((err) => {
        throw new Error(err?.message);
      });
  };
  return {
    updateOrderStatus,
  };
};
